/**
 * One tier scale for every surface (DASHBOARD_PARITY §2): thresholds come from the admin setting
 * `scores.tiers` (the API returns them); OutlierOS used 1.8 here and 1.5 elsewhere.
 */
import { TIER_EMOJI, TIER_LABELS, formatWiseScore, tierEmoji, wiseScoreLabel } from '@vw/core/src/score';

export { TIER_EMOJI, formatWiseScore, tierEmoji, wiseScoreLabel };
import { cn } from './cn';

export interface TierThresholds { legendary: number; viral: number; outlier: number; above: number }
export type TierName = 'legendary' | 'viral' | 'outlier' | 'above' | 'normal';

export function tierOf(score: number | null | undefined, t: TierThresholds): TierName | null {
  if (score === null || score === undefined || !Number.isFinite(score)) return null;
  if (score >= t.legendary) return 'legendary';
  if (score >= t.viral) return 'viral';
  if (score >= t.outlier) return 'outlier';
  if (score >= t.above) return 'above';
  return 'normal';
}

/** Display names (decision 62): Legendary, Viral, Hot (key `outlier`), Above usual, Normal. */
export const TIER_LABEL: Record<TierName, string> = TIER_LABELS;
const TIER_CLASS: Record<TierName, string> = {
  legendary: 'bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white shadow-lg shadow-violet-500/30',
  viral: 'bg-gradient-to-r from-red-600 to-orange-500 text-white shadow-lg shadow-red-500/30',
  outlier: 'bg-gradient-to-r from-amber-400 to-yellow-300 text-black',
  above: 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white',
  normal: 'bg-secondary text-muted-foreground',
};


/** The Wise Score badge used on reel cards, bookmarks, compare and share pages. */
export function TierBadge({ score, tiers, className }: { score: number | null; tiers: TierThresholds; className?: string }) {
  const tier = tierOf(score, tiers);
  if (!tier || score === null) return null;
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs font-black backdrop-blur', TIER_CLASS[tier], className)} title={`Wise Score ${score.toFixed(2)}× (${TIER_LABEL[tier]}): views ÷ the creator's usual views`}>
      {TIER_EMOJI[tier] && <span aria-hidden="true">{TIER_EMOJI[tier]}</span>}
      {formatWiseScore(score)}
    </span>
  );
}
